/* ============================================================
   DarthDabs — Age Gate behavior
   - Reads localStorage.dd_age_verified on load.
   - If "true", removes pending class + hides gate immediately.
   - Otherwise wires up Accept / Reject buttons.
   - Reject is terminal in-session (no way back to State 1).
   - Honors prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'dd_age_verified';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function hasReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { return false; }
  }

  function isVerified() {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; }
    catch (e) { return false; }
  }

  function persistAccept() {
    try { localStorage.setItem(STORAGE_KEY, 'true'); }
    catch (e) { /* fail open — user has accepted in-session even if storage blocked */ }
  }

  function unlockScroll() {
    document.documentElement.classList.remove('dd-age-gate-pending');
    document.body.classList.remove('dd-age-gate-active');
  }

  function dismissGate(gate, onDismissed) {
    if (hasReducedMotion()) {
      gate.hidden = true;
      onDismissed();
      return;
    }
    gate.classList.add('is-fading-out');
    var timer;
    var finished = false;
    var done = function (event) {
      // A descendant's transition must not prematurely dismiss this dialog.
      if (event && (event.target !== gate || event.propertyName !== 'opacity')) return;
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      gate.hidden = true;
      gate.classList.remove('is-fading-out');
      gate.removeEventListener('transitionend', done);
      onDismissed();
    };
    gate.addEventListener('transitionend', done);
    // Safety: if transitionend doesn't fire, hide after 400ms.
    timer = setTimeout(done, 400);
  }

  function showState(gate, name) {
    var states = gate.querySelectorAll('[data-dd-age-state]');
    for (var i = 0; i < states.length; i++) {
      var el = states[i];
      var match = el.getAttribute('data-dd-age-state') === name;
      el.hidden = !match;
    }
  }

  ready(function () {
    var gate = document.getElementById('dd-age-gate');
    if (!gate) {
      unlockScroll();
      return;
    }

    // Already verified? Dismiss immediately, no fade.
    if (isVerified()) {
      unlockScroll();
      gate.hidden = true;
      return;
    }

    var previousFocus = document.activeElement;
    var state = 'verify';
    var active = true;
    var rescuingFocus = false;
    var background = new Map();
    var observer;

    function focus(element) {
      if (!element) return;
      try { element.focus({ preventScroll: true }); } catch (e) { element.focus(); }
    }

    function visible(element) {
      if (!element || !element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      var style = window.getComputedStyle(element);
      return style.visibility !== 'hidden' && style.visibility !== 'collapse' && element.getClientRects().length > 0;
    }

    function headline() {
      return gate.querySelector('[data-dd-age-state="' + state + '"] .dd-age-gate__headline');
    }

    function focusHeadline() {
      var target = headline() || gate;
      target.setAttribute('tabindex', '-1');
      focus(target);
    }

    function labelState() {
      var title = headline();
      if (!title) return;
      if (!title.id) title.id = 'dd-age-gate-' + state + '-headline';
      title.setAttribute('tabindex', '-1');
      gate.setAttribute('aria-labelledby', title.id);
    }

    function isolateBackground() {
      // The gate is normally a body child. Walking its ancestor chain also
      // protects sibling content if a theme editor wraps it in a section.
      var branch = gate;
      while (branch.parentElement) {
        var parent = branch.parentElement;
        if (parent === document.documentElement) break;
        for (var i = 0; i < parent.children.length; i++) {
          var sibling = parent.children[i];
          if (sibling === branch) continue;
          if (!background.has(sibling)) {
            background.set(sibling, {
              inert: sibling.getAttribute('inert'),
              ariaHidden: sibling.getAttribute('aria-hidden')
            });
          }
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
        }
        if (parent === document.body) break;
        branch = parent;
      }
    }

    function restoreAttribute(element, name, original, applied) {
      // Do not overwrite a distinct state another component set in the interim.
      if (element.getAttribute(name) !== applied) return;
      if (original === null) element.removeAttribute(name);
      else element.setAttribute(name, original);
    }

    function onFocusIn(event) {
      if (!active || rescuingFocus) return;
      if (gate.contains(event.target) && visible(event.target)) return;
      rescuingFocus = true;
      focusHeadline();
      rescuingFocus = false;
    }

    function onKeyDown(event) {
      if (!active) return;
      // Escape does not dismiss the age decision or the terminal reject state.
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== 'Tab') return;
      event.stopPropagation();
      var candidates = gate.querySelectorAll('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]');
      var tabbable = Array.prototype.filter.call(candidates, function (element) {
        return element.tabIndex >= 0 && !element.matches(':disabled') && visible(element);
      });
      var current = document.activeElement;
      var index = tabbable.indexOf(current);
      if (!tabbable.length) {
        event.preventDefault();
        focusHeadline();
      } else if (index === -1 || (event.shiftKey && index === 0) || (!event.shiftKey && index === tabbable.length - 1)) {
        event.preventDefault();
        focus(tabbable[event.shiftKey ? tabbable.length - 1 : 0]);
      }
    }

    function releaseModal() {
      active = false;
      if (observer) observer.disconnect();
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('keydown', onKeyDown, true);
      background.forEach(function (original, element) {
        restoreAttribute(element, 'inert', original.inert, '');
        restoreAttribute(element, 'aria-hidden', original.ariaHidden, 'true');
      });
      background.clear();
      unlockScroll();
      if (previousFocus && previousFocus !== document.body && !gate.contains(previousFocus) && visible(previousFocus) && !previousFocus.matches(':disabled')) {
        focus(previousFocus);
      } else {
        // Initial page loads usually have no opener. Continue at the main
        // content instead of leaving focus on a now-hidden decision button.
        var main = document.getElementById('MainContent') || document.querySelector('main');
        var destination = visible(main) ? main : document.body;
        if (visible(destination)) {
          var originalTabindex = destination.getAttribute('tabindex');
          var releaseTabindex;
          if (originalTabindex === null) {
            destination.setAttribute('tabindex', '-1');
            // Chromium can drop focus when tabindex is removed from the
            // focused main/body. Keep it until the visitor actually leaves.
            releaseTabindex = function () {
              destination.removeEventListener('blur', releaseTabindex);
              restoreAttribute(destination, 'tabindex', null, '-1');
            };
            destination.addEventListener('blur', releaseTabindex);
          }
          focus(destination);
          if (releaseTabindex && document.activeElement !== destination) releaseTabindex();
        }
      }
    }

    // Not verified — engage scroll lock + ensure verify state visible.
    document.body.classList.add('dd-age-gate-active');
    gate.hidden = false;
    showState(gate, 'verify');
    labelState();
    // Move focus before aria-hiding its former ancestor (avoids an AT warning).
    focusHeadline();
    isolateBackground();
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('keydown', onKeyDown, true);
    if (window.MutationObserver) {
      observer = new MutationObserver(function () {
        if (active) isolateBackground();
      });
      // Covers lazily appended chat/marketing widgets without observing our own
      // inert/aria-hidden attribute changes or modifying consent storage.
      observer.observe(document.body, { childList: true, subtree: true });
    }

    var acceptBtn = gate.querySelector('[data-dd-age-accept]');
    var rejectBtn = gate.querySelector('[data-dd-age-reject]');

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        if (state !== 'verify') return;
        state = 'dismissing';
        persistAccept();
        dismissGate(gate, releaseModal);
      });
    }

    if (rejectBtn) {
      rejectBtn.addEventListener('click', function () {
        if (state !== 'verify') return;
        // Terminal: switch to reject state and leave it up. No persist.
        state = 'reject';
        showState(gate, 'reject');
        labelState();
        focusHeadline();
      });
    }
  });
})();
